import React, { createContext, useContext, FunctionComponent, useState, useMemo, useCallback, useEffect } from 'react';
import { Map } from 'immutable';
import { useMemoOne } from 'app/utils/useMemoOne';
import isEqual from 'lodash/isEqual';
import { useQuery } from 'app/components/Contexts';
import { useHistory, useHistoryLocation } from 'app/components/router/History';
import { updateWithPreservedParams } from 'app/models/route/actions';
import { parse } from 'query-string';

interface IPreservingSearchParamsContext {
  state: Map<string, string>;
  setState: (update: (map: Map<string, string>) => Map<string, string>) => void;
}

const Context = createContext<IPreservingSearchParamsContext>({
  state: Map(),
  setState: () => {},
});

export const PreservingSearchParamsProvider: FunctionComponent = ({ children }) => {
  const [state, setState] = useState(Map<string, string>());

  const value = useMemo(() => ({
    state,
    setState,
  }), [state, setState]);

  return <Context.Provider value={value}>
    {children}
  </Context.Provider>;
};

type Values<Params extends readonly string[]> = { [K in Params[number]]?: string };

export const usePreservingSearchParams = <Params extends readonly string[]>(params: Params) => {
  const { state, setState } = useContext(Context);

  const update = useCallback((values: Values<Params>) => {
    setState(prev => prev.withMutations(all => params.forEach((param: Params[number]) => {
      const value: string | undefined = values[param];

      if (value !== undefined) {
        all.set(param, value);
      } else {
        all.delete(param);
      }
    })));
  }, [setState, params]);

  const values = useMemo(() => params.reduce((agg: Values<Params>, param) => {
    const value = state.get(param);

    if (value !== undefined) {
      return { ...agg, [param]: value };
    } else {
      return agg;
    }
  }, {}), [params, state]);

  return useMemo(() => [values, update] as const, [update, values]);
};

const getQueryParams = <Params extends readonly string[]>(params: Params, query: { [x: string]: unknown }) =>
  params.reduce((agg: Values<Params>, param) => {
    const value = query[param];

    if (typeof value === 'string') {
      return { ...agg, [param]: value };
    } else {
      return agg;
    }
  }, {});

export const usePreservingSearchParamsWithSideEffects = <Params extends readonly string[]>(params: Params) => {
  const [values, setValues] = usePreservingSearchParams(params);
  const history = useHistory();
  const query = useQuery();
  const { pathname } = useHistoryLocation();

  useEffect(() => {
    if (
      // Only apply if all values are set
      Object.keys(values).length === params.length &&
      // Only apply if preserved params are different. Reach directly
      // to current location because we don't want to invalidate
      // this useEffect when query params change, only when stored
      // params change.
      !isEqual(values, getQueryParams(params, parse(history.location.search)))
    ) {
      updateWithPreservedParams(history, values)();
    }
    // Invalidate base on pathname too because some pages like DN
    // category page will stay mounted even if pathname changes
  }, [params, values, history, pathname]);

  const queryValues = useMemo(() => getQueryParams(params, query), [params, query]);
  // Memoize query based on its own value to prevent below
  // useEffect's invalidation invalidation
  const memoizedQuery = useMemoOne(() => queryValues, [queryValues], isEqual);

  useEffect(() => {
    setValues(memoizedQuery);
  }, [memoizedQuery, setValues]);

  return values;
};
